import React, { useState, useMemo, useEffect } from 'react';
import { useApp, hasPrefix, previewDocNo } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import FormField, { ErrorSummary } from '../common/FormField';
import PrintPreview from '../common/PrintPreview';
import { Plus, Edit2, Trash2, Printer } from 'lucide-react';
import { formatCurrency, formatDate, today } from '../../utils/helpers';
import { validators, validateForm } from '../../utils/validation';

const emptyForm = { date: today(), partyId: '', amount: 0, mode: 'Cash', bankAccount: '', bankDetails: '', chequeNo: '', againstInvoice: '', allocations: [], narration: '' };

const outstandingOf = (inv) => (inv.grandTotal || 0) - (inv.paidAmount || 0);

export default function ReceiptEntry() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [printData, setPrintData] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  // Allocation map: { [invoiceNo]: amount } — the invoices this receipt settles.
  const [alloc, setAlloc] = useState({});

  const unpaidInvoices = useMemo(
    () => state.salesInvoices.filter(i => i.status !== 'Paid' && (!form.partyId || i.partyId === form.partyId)),
    [state.salesInvoices, form.partyId]
  );

  const totalAllocated = Object.values(alloc).reduce((s, v) => s + (Number(v) || 0), 0);

  // When invoices are selected, keep the receipt Amount in sync with the total
  // allocated (so "one receipt amount" settles the picked invoices).
  useEffect(() => {
    if (Object.keys(alloc).length > 0) {
      const t = Object.values(alloc).reduce((s, v) => s + (Number(v) || 0), 0);
      setForm(f => (Number(f.amount) === t ? f : { ...f, amount: t }));
    }
  }, [alloc]);

  const toggleInvoice = (inv) => {
    setAlloc(prev => {
      const next = { ...prev };
      if (inv.invoiceNo in next) delete next[inv.invoiceNo];
      else next[inv.invoiceNo] = outstandingOf(inv);
      return next;
    });
  };
  const setInvoiceAmount = (inv, val) => {
    const capped = Math.min(Number(val) || 0, outstandingOf(inv));
    setAlloc(prev => ({ ...prev, [inv.invoiceNo]: capped < 0 ? 0 : capped }));
  };

  const errors = useMemo(() => {
    const rules = {
      date: [validators.required(form.date, 'Date')],
      partyId: [validators.required(form.partyId, 'Party')],
      amount: [
        validators.required(form.amount, 'Amount'),
        validators.positiveNonZero(form.amount, 'Amount'),
        validators.maxValue(form.amount, 99999999, 'Amount'),
      ],
      narration: [validators.maxLength(form.narration, 500, 'Narration')],
    };
    if (form.mode === 'Cheque') {
      rules.chequeNo = [validators.required(form.chequeNo, 'Cheque number')];
      rules.bankDetails = [validators.required(form.bankDetails, 'Bank details')];
    }
    if (form.mode === 'NEFT') {
      rules.chequeNo = [validators.required(form.chequeNo, 'Reference number')];
    }
    if (form.mode !== 'Cash') {
      rules.bankAccount = [validators.required(form.bankAccount, 'Bank account')];
    }

    const { errors } = validateForm(rules);
    return errors;
  }, [form]);

  const hasErrors = Object.values(errors).some(e => e);
  const touch = (field) => setTouched(prev => ({ ...prev, [field]: true }));

  const prefixMissing = !editData && !hasPrefix(state, 'receipt', form.date);

  const handleSave = () => {
    if (prefixMissing) { alert('No Prefix Setting found for Receipt. Add one in Masters → Prefix Setting (effective on the selected date) before saving.'); return; }
    setSubmitted(true);
    const all = {};
    ['date', 'partyId', 'amount', 'chequeNo', 'bankDetails', 'bankAccount'].forEach(k => { all[k] = true; });
    setTouched(all);
    if (hasErrors) return;
    setSaving(true);
    // Build the allocations array from the picked invoices.
    const allocations = Object.entries(alloc)
      .map(([invoiceNo, amount]) => ({ invoiceNo, amount: Number(amount) || 0 }))
      .filter(a => a.amount > 0);
    const payload = {
      ...form,
      allocations,
      againstInvoice: allocations.map(a => a.invoiceNo).join(', '),
    };
    if (editData) {
      dispatch({ type: 'DELETE_RECEIPT', payload: editData.id });
    }
    dispatch({ type: 'ADD_RECEIPT', payload });
    addToast('Receipt saved successfully', 'success');
    closeForm();
  };

  const handleEdit = (row) => {
    setForm({
      date: row.date,
      partyId: row.partyId,
      amount: row.amount,
      mode: row.mode,
      bankAccount: row.bankAccount || '',
      bankDetails: row.bankDetails || '',
      chequeNo: row.chequeNo || '',
      againstInvoice: row.againstInvoice || '',
      allocations: row.allocations || [],
      narration: row.narration || '',
    });
    // Restore the allocation grid. Fall back to the legacy single-invoice field.
    if (Array.isArray(row.allocations) && row.allocations.length) {
      setAlloc(Object.fromEntries(row.allocations.map(a => [a.invoiceNo, a.amount])));
    } else if (row.againstInvoice) {
      setAlloc({ [row.againstInvoice]: row.amount });
    } else {
      setAlloc({});
    }
    setEditData(row);
    setTouched({});
    setSubmitted(false);
    setShowForm(true);
  };

  const handleDelete = (row) => {
    if (window.confirm(`Delete receipt ${row.receiptNo}? Account balance will be reversed.`)) {
      dispatch({ type: 'DELETE_RECEIPT', payload: row.id });
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setForm(emptyForm);
    setAlloc({});
    setEditData(null);
    setTouched({});
    setSubmitted(false);
  };

  const columns = [
    { key: 'receiptNo', label: 'Receipt No', sortable: true },
    { key: 'date', label: 'Date', render: (v) => formatDate(v) },
    { key: 'partyId', label: 'Received From', render: (v) => state.parties.find(p => p.id === v)?.name || '-' },
    { key: 'amount', label: 'Amount', align: 'right', render: (v) => formatCurrency(v) },
    { key: 'mode', label: 'Mode', render: (v) => <span className="badge badge-gray">{v}</span> },
    { key: 'againstInvoice', label: 'Against Invoice' },
  ];

  if (printData) return <PrintPreview type="receipt" data={printData} onClose={() => setPrintData(null)} />;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Party Receipt Entry</h1>
        <button onClick={() => { setForm(emptyForm); setAlloc({}); setEditData(null); setTouched({}); setSubmitted(false); setShowForm(true); }} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} /> New Receipt
        </button>
      </div>
      <div className="card">
        <DataTable
          columns={columns}
          data={[...state.receipts].reverse()}
          searchFields={['receiptNo']}
          emptyState={{ title: 'No receipts yet', description: 'Record your first receipt entry.', actionLabel: 'New Receipt', onAction: () => { setForm(emptyForm); setAlloc({}); setTouched({}); setSubmitted(false); setShowForm(true); } }}
          actions={(row) => (
            <>
              <button onClick={() => handleEdit(row)} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => setPrintData(row)} className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Print"><Printer size={15} /></button>
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>

      <Modal isOpen={showForm} onClose={closeForm} title={editData ? `Edit Receipt ${editData.receiptNo}` : 'New Receipt Entry'}>
        <ErrorSummary errors={errors} show={submitted && hasErrors} />
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Receipt No">
              <input className="input bg-gray-100 font-semibold text-blue-700" value={editData ? editData.receiptNo : previewDocNo(state, 'receipt', 'receipts', form.date, 'RCT')} readOnly placeholder="Set Prefix first" />
            </FormField>

            <FormField label="Date" required error={errors.date} touched={touched.date || submitted}>
              <input className="input" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} onBlur={() => touch('date')} max={today()} />
            </FormField>

            <FormField label="Received From" required error={errors.partyId} touched={touched.partyId || submitted}>
              <select className="input" value={form.partyId} onChange={e => setForm({...form, partyId: e.target.value, againstInvoice: ''})} onBlur={() => touch('partyId')}>
                <option value="">-- Select --</option>
                {state.parties.filter(p => p.type === 'Customer' || p.type === 'Both').map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Amount" required error={errors.amount} touched={touched.amount || submitted}>
              <input className="input" type="number" min="0.01" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: Number(e.target.value)})} onBlur={() => touch('amount')} />
            </FormField>

            <FormField label="Mode" required>
              <select className="input" value={form.mode} onChange={e => setForm({...form, mode: e.target.value, bankAccount: e.target.value === 'Cash' ? '' : form.bankAccount})}>
                <option>Cash</option>
                <option>Cheque</option>
                <option>NEFT</option>
                <option>UPI</option>
              </select>
            </FormField>

            {form.mode !== 'Cash' && (
              <FormField label="To Bank" required error={errors.bankAccount} touched={touched.bankAccount || submitted} hint="Bank account that receives this money">
                <select className="input" value={form.bankAccount} onChange={e => setForm({...form, bankAccount: e.target.value})} onBlur={() => touch('bankAccount')}>
                  <option value="">-- Select Bank --</option>
                  {state.accounts.filter(a => a.type === 'Bank').map(a => (
                    <option key={a.id} value={a.name}>{a.name} (Balance: {formatCurrency(a.balance)})</option>
                  ))}
                </select>
              </FormField>
            )}

            {(form.mode === 'Cheque' || form.mode === 'NEFT') && (
              <>
                <FormField label="Bank Details" required={form.mode === 'Cheque'} error={errors.bankDetails} touched={touched.bankDetails || submitted}>
                  <input className="input" value={form.bankDetails} onChange={e => setForm({...form, bankDetails: e.target.value})} onBlur={() => touch('bankDetails')} placeholder="Bank name & branch" />
                </FormField>
                <FormField label={form.mode === 'Cheque' ? 'Cheque No' : 'Reference No'} required error={errors.chequeNo} touched={touched.chequeNo || submitted}>
                  <input className="input" value={form.chequeNo} onChange={e => setForm({...form, chequeNo: e.target.value})} onBlur={() => touch('chequeNo')} />
                </FormField>
              </>
            )}

            {form.partyId && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Against Invoice(s) — tick to settle</label>
                {unpaidInvoices.length === 0 ? (
                  <p className="text-sm text-gray-400 border border-dashed rounded-lg p-3">No unpaid invoices for this party.</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="max-h-56 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr className="text-gray-500 text-xs uppercase">
                            <th className="p-2 w-8"></th>
                            <th className="p-2 text-left">Invoice No</th>
                            <th className="p-2 text-left">Date</th>
                            <th className="p-2 text-right">Total</th>
                            <th className="p-2 text-right">Outstanding</th>
                            <th className="p-2 text-right w-32">Allocate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unpaidInvoices.map(inv => {
                            const checked = inv.invoiceNo in alloc;
                            return (
                              <tr key={inv.id} className={`border-t border-gray-100 ${checked ? 'bg-blue-50/50' : ''}`}>
                                <td className="p-2 text-center">
                                  <input type="checkbox" checked={checked} onChange={() => toggleInvoice(inv)} className="cursor-pointer" />
                                </td>
                                <td className="p-2 font-medium text-gray-800 cursor-pointer" onClick={() => toggleInvoice(inv)}>{inv.invoiceNo}</td>
                                <td className="p-2 text-gray-500">{formatDate(inv.date)}</td>
                                <td className="p-2 text-right">{formatCurrency(inv.grandTotal)}</td>
                                <td className="p-2 text-right text-amber-700">{formatCurrency(outstandingOf(inv))}</td>
                                <td className="p-2 text-right">
                                  <input
                                    type="number" min="0" step="0.01"
                                    className="input !py-1 !px-2 text-right text-xs w-28"
                                    value={checked ? alloc[inv.invoiceNo] : ''}
                                    disabled={!checked}
                                    onChange={e => setInvoiceAmount(inv, e.target.value)}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex justify-between items-center px-3 py-2 bg-gray-50 border-t border-gray-200 text-sm">
                      <span className="text-gray-500">{Object.keys(alloc).length} invoice(s) selected</span>
                      <span className="font-semibold text-gray-800">Total Allocated: {formatCurrency(totalAllocated)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <FormField label="Narration" error={errors.narration} touched={touched.narration || submitted}>
            <textarea className="input" rows={2} value={form.narration} onChange={e => setForm({...form, narration: e.target.value})} onBlur={() => touch('narration')} maxLength={500} />
          </FormField>
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={closeForm} className="btn btn-secondary">Cancel</button>
          <button data-keyboard-save onClick={handleSave} className="btn btn-primary" disabled={(submitted && hasErrors) || saving}>{editData ? 'Update' : 'Save'}</button>
        </div>
      </Modal>
    </div>
  );
}
